import type { AuthCredential } from 'firebase/auth'
import type { FirebaseAuthenticationPlugin, SignInResult } from '@capacitor-firebase/authentication'

type GoogleAuthModule = Pick<typeof import('firebase/auth'), 'GoogleAuthProvider'>
type AppleAuthModule = Pick<typeof import('firebase/auth'), 'OAuthProvider'>
let pendingNativeSignIn: Promise<SignInResult> | null = null
const signInTimeout = () => Object.assign(new Error('Sign-in timed out. Please try again.'), {
  code: 'auth/native-sign-in-timeout',
})
const noCredential = () => Object.assign(new Error('The sign-in provider returned no credential.'), { code: 'auth/invalid-credential' })

/** Native OAuth only supplies credentials. Firebase JS owns the user and its UID.
 * A late plugin response after the watchdog cannot sign in, link or delete anyone.
 * 🍎 M7: one account sheet for Google and Apple alike - `start` picks the plugin
 * call, `finish` reads the credential out of its answer. */
async function nativeSignIn<T>(start: (plugin: FirebaseAuthenticationPlugin) => Promise<SignInResult>, finish: (result: SignInResult) => T): Promise<T> {
  // The plugin has no cancel method. A watchdog releases our UI, but must not
  // allow another native account sheet while the old plugin call is outstanding.
  if (pendingNativeSignIn) throw signInTimeout()
  const operation = import('@capacitor-firebase/authentication').then(({ FirebaseAuthentication }) => start(FirebaseAuthentication))
  pendingNativeSignIn = operation
  const settled = () => { if (pendingNativeSignIn === operation) pendingNativeSignIn = null }
  void operation.then(settled, settled)
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const result = await Promise.race([
      operation,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(signInTimeout()), 20_000)
      }),
    ])
    return finish(result)
  } catch (error) {
    const failure = error as { code?: string; message?: string }
    if (typeof failure?.code === 'string' && failure.code.startsWith('auth/')) throw error
    // Native plugins also reject plain messages/numeric provider codes. Keep
    // deletion's existing reauth error handling independent of that format.
    throw Object.assign(new Error(failure?.message || 'The sign-in provider could not finish.'), {
      code: cancelledSignIn(error) ? 'auth/cancelled' : 'auth/native-sign-in-failed',
    })
  } finally {
    if (timer) clearTimeout(timer)
  }
}

export async function nativeGoogleCredential(authMod: GoogleAuthModule): Promise<AuthCredential> {
  return nativeSignIn(plugin => plugin.signInWithGoogle({ skipNativeAuth: true }), (result) => {
    const idToken = result.credential?.idToken
    if (!idToken) throw noCredential()
    return authMod.GoogleAuthProvider.credential(idToken)
  })
}

/** 🍎 M7: what an iPhone's Sign in with Apple hands over besides the credential -
 * the authorization code deletion needs (Apple TN3194, kept server-side as a
 * refresh token) and the name Apple gives on the FIRST authorization only. */
export interface AppleNativeResult {
  credential: AuthCredential
  authorizationCode: string | null
  displayName: string | null
}

export async function nativeAppleCredential(authMod: AppleAuthModule): Promise<AppleNativeResult> {
  return nativeSignIn(plugin => plugin.signInWithApple({ skipNativeAuth: true }), (result) => {
    const idToken = result.credential?.idToken
    const rawNonce = result.credential?.nonce
    if (!idToken || !rawNonce) throw noCredential()
    const profile = (result.additionalUserInfo?.profile ?? {}) as Record<string, unknown>
    const fromProfile = typeof profile.displayName === 'string' ? profile.displayName : typeof profile.name === 'string' ? profile.name : null
    const displayName = (result.user?.displayName ?? fromProfile ?? '').trim()
    return {
      credential: new authMod.OAuthProvider('apple.com').credential({ idToken, rawNonce }),
      authorizationCode: result.credential?.authorizationCode ?? null,
      displayName: displayName || null,
    }
  })
}

/** Closing either the account sheet or the web popup is a normal choice. */
export function cancelledSignIn(error: unknown): boolean {
  const { code, message } = (error ?? {}) as { code?: string; message?: string }
  return code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request'
    || /cancelled|canceled/i.test(code ?? '') || /cancelled|canceled/i.test(message ?? '')
}
